"use client";

import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { thetaApi } from "@/lib/api/theta";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { MitidButton } from "./MitidButton";
import { retryUnlessRefused } from "@/lib/hooks/queryRetry";

const DISMISSED_KEY = "mitid_banner_dismissed";

export function MitidVerificationBanner() {
  const pathname = usePathname();
  const { data: currentUser } = useCurrentUser();
  const [dismissed, setDismissed] = useState(true); // start hidden to avoid flash

  useEffect(() => {
    // localStorage is unavailable during SSR; reading it in render would produce
    // a hydration mismatch. Starts hidden and corrects itself on mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDismissed(localStorage.getItem(DISMISSED_KEY) === "true");
  }, []);

  /**
   * BY ID (O-21a) and on the SAME query key the profile page uses, so the two
   * are one request and one cache entry — `verified_at` on the legacy profile
   * is `mitIdVerifiedAt` here. The old key was the username, which is not an
   * address any more and is not on the wire at all.
   */
  const { data } = useQuery({
    queryKey: ["v1", "user", currentUser?.id],
    queryFn: () => thetaApi.user(currentUser!.id),
    enabled: !!currentUser && !dismissed,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessRefused,
  });

  const verified = !!data?.mitIdVerifiedAt;

  if (!currentUser || dismissed || verified) return null;
  if (pathname === '/verificer') return null;
  // Don't show until we know the verification status
  if (!data) return null;

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-950 border-b border-blue-200 dark:border-blue-800">
      <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
            Verificer din identitet
          </p>
          <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
            Bekræft, at du er dig, med MitID — det tager under et minut.
          </p>
        </div>
        <div className="shrink-0">
          <MitidButton />
        </div>
        <button
          onClick={dismiss}
          aria-label="Luk"
          className="shrink-0 p-1 text-blue-400 hover:text-blue-600 dark:hover:text-blue-200 transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
